import { useState, useEffect } from 'react';

/**
 * Hook to detect if debug mode is enabled via localStorage
 * Debug mode is enabled when localStorage has key 'debug' with value 'true'
 * 
 * Auto-updates when localStorage changes (in current tab via custom event, 
 * or in other tabs via storage event)
 * 
 * @returns true if debug mode is enabled, false otherwise
 * 
 * @example
 * // Enable debug mode in console:
 * localStorage.setItem('debug', 'true');
 * window.dispatchEvent(new Event('debug-mode-changed'));
 * 
 * // Disable debug mode in console:
 * localStorage.removeItem('debug');
 * window.dispatchEvent(new Event('debug-mode-changed'));
 */
export const useDebugMode = () => {
  const [isDebugMode, setIsDebugMode] = useState(() => {
    try {
      return localStorage.getItem('debug') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const checkDebugMode = () => {
      try {
        setIsDebugMode(localStorage.getItem('debug') === 'true');
      } catch {
        setIsDebugMode(false);
      }
    };

    // Listen for storage events (changes from other tabs)
    window.addEventListener('storage', checkDebugMode);
    
    // Listen for custom event (changes from current tab)
    window.addEventListener('debug-mode-changed', checkDebugMode);

    return () => {
      window.removeEventListener('storage', checkDebugMode);
      window.removeEventListener('debug-mode-changed', checkDebugMode);
    };
  }, []);

  return isDebugMode;
};
